import { Emergency } from "@mui/icons-material";
import { AppBar, Box, Button, Toolbar, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { NavLink } from "react-router-dom";

import { Route } from "~services/router/Route";
import { EntityAvatar } from "~ui/atoms/EntityAvatar/EntityAvatar";
import { AppLogo } from "~ui/organisms/AppLogo/AppLogo";
import { makeBubbleAvatarUrl } from "~utils/url";

const tEmergency = "Emergency";
const tLogoTooltip = "Click to return to home page";
const tSchoolVmHomeTooltip = "Go to visitor management home";

export interface VisitorManagementTopBarProps {
  isKioskLayout: boolean;
  logoHeight: string;
  onEmergency: () => void;
  schoolId: string | undefined;
  schoolName: string;
}

export function VisitorManagementTopBar({
  isKioskLayout,
  logoHeight,
  onEmergency,
  schoolId,
  schoolName,
}: VisitorManagementTopBarProps) {
  const theme = useTheme();
  const isBelowXl = useMediaQuery(theme.breakpoints.down("xl"));
  const showSchoolInHeader = !isKioskLayout && !isBelowXl;

  return (
    <AppBar
      color="inherit"
      elevation={0}
      position="static"
      sx={{
        bgcolor: "background.paper",
        borderBottom: 1,
        borderColor: "divider",
        flexShrink: 0,
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          alignItems: "center",
          columnGap: 1,
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
          minHeight: { sm: 56, xs: 56 },
          px: { sm: 2, xs: 1 },
          py: 1,
        }}
      >
        <Box sx={{ justifySelf: "start" }}>
          <Tooltip placement="bottom" title={tLogoTooltip}>
            <Button
              aria-label={tLogoTooltip}
              color="inherit"
              component={NavLink}
              sx={{ borderRadius: 1, minWidth: "auto", px: 1, py: 0.5 }}
              to={Route.home()}
              variant="text"
            >
              <AppLogo logo="navBarLogo" style={{ height: logoHeight }} />
            </Button>
          </Tooltip>
        </Box>

        {showSchoolInHeader ? (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              justifyContent: "center",
              maxWidth: "100%",
              minWidth: 0,
            }}
          >
            {schoolId ? (
              <Tooltip placement="bottom" title={tSchoolVmHomeTooltip}>
                <Button
                  aria-label={tSchoolVmHomeTooltip}
                  color="inherit"
                  component={NavLink}
                  sx={{
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                    alignItems: "center",
                    borderRadius: 1,
                    color: "inherit",
                    display: "flex",
                    flexDirection: "row",
                    gap: 2,
                    justifyContent: "center",
                    maxWidth: "100%",
                    minWidth: 0,
                    px: 1.5,
                    py: 0.5,
                    textAlign: "center",
                    textTransform: "none",
                  }}
                  to={Route.schoolVisitorManagement({ schoolId })}
                  variant="text"
                >
                  <EntityAvatar
                    canEdit={false}
                    entityName={schoolName}
                    imageUrl={makeBubbleAvatarUrl(schoolId)}
                    imgAlt=""
                    size="small"
                  />
                  <Typography component="span" noWrap variant="page-header">
                    {schoolName}
                  </Typography>
                </Button>
              </Tooltip>
            ) : (
              <Typography component="h1" noWrap variant="page-header">
                {schoolName}
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ minWidth: 0 }} />
        )}

        <Box sx={{ justifySelf: "end" }}>
          <Button
            color="error"
            size="medium"
            startIcon={<Emergency />}
            sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
            variant="contained"
            onClick={onEmergency}
          >
            {tEmergency}
          </Button>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
